import { Component, OnInit } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { SupportService } from '../shared/support.service';
import { AutocloseTicket, formatSupportCaseNames } from '../shared/support.model';
import { NotificationService } from '../../shared/services/notification.service';

@Component({
  standalone: false,
  selector: 'app-autoclose-tickets-table',
  templateUrl: 'autoclose-tickets-table.component.html',
})
export class AutocloseTicketsTableComponent implements OnInit {
  gridApi: GridApi;
  tickets: AutocloseTicket[] = [];
  loading = false;
  limit = 50;
  offset = 0;

  selectedTicketId: number | null = null;
  showDetailModal = false;

  columnDefs: ColDef[] = [
    {
      headerName: 'Ticket ID',
      field: 'ticketId',
      width: 110,
      cellRenderer: params => {
        if (!params.value) return '';
        return `<a href="javascript:void(0)" class="ticket-link">#${params.value}</a>`;
      },
      onCellClicked: params => {
        this.openTicketDetail(params.data.ticketId);
      },
    },
    {
      headerName: 'Subject',
      valueGetter: params => params.data?.decision?.subject || '—',
      flex: 2,
      minWidth: 200,
    },
    {
      headerName: 'Cases',
      valueGetter: params => formatSupportCaseNames(params.data?.decision),
      width: 220,
    },
    {
      headerName: 'Confidence',
      valueGetter: params => {
        const c = params.data?.decision?.confidence;
        return typeof c === 'number' ? (c * 100).toFixed(0) + '%' : '—';
      },
      width: 100,
    },
    {
      headerName: 'Action',
      valueGetter: params => params.data?.decision?.action?.type || '—',
      width: 100,
      cellRenderer: params => {
        const type = params.value;
        if (!type || type === '—') return '—';
        const badgeClass =
          {
            reply: 'badge bg-primary',
            close: 'badge bg-secondary',
          }[type] || 'badge bg-info';
        return `<span class="${badgeClass}">${type}</span>`;
      },
    },
    {
      headerName: 'Closed At',
      field: 'cachedAt',
      width: 160,
      sort: 'desc',
      valueFormatter: params => {
        if (!params.value) return '—';
        return new Date(params.value).toLocaleString();
      },
    },
  ];

  defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    suppressMenu: true,
    cellClass: 'd-flex justify-content-start align-items-center',
  };

  constructor(
    private supportService: SupportService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    this.loadTickets();
  }

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event.api;
  }

  loadTickets() {
    this.loading = true;
    this.supportService.getAutocloseTickets({ limit: this.limit, offset: this.offset }).subscribe({
      next: res => {
        this.tickets = res.items;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notificationService.error('Failed to load autoclose tickets', 'Support');
      },
    });
  }

  loadMore() {
    this.offset += this.limit;
    this.loading = true;
    this.supportService.getAutocloseTickets({ limit: this.limit, offset: this.offset }).subscribe({
      next: res => {
        this.tickets = [...this.tickets, ...res.items];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  openTicketDetail(ticketId: number) {
    this.selectedTicketId = ticketId;
    this.showDetailModal = true;
  }

  closeDetailModal() {
    this.showDetailModal = false;
    this.selectedTicketId = null;
  }
}
